import { Classes, Colors, Dialog, Intent, ButtonGroup } from "@blueprintjs/core";
import { ButtonContainer, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import {
  BreedVarietiesAndExceptionsGridDto,
  BreedVarietyAndExceptionSearch,
  BreedVarietyApi,
} from "../../../../../api";
import { ERoutes } from "../../../../../ERoutes";
import { CustomDataTable, DeleteButton, EditButton, ExportButton } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useDeleteApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { BreedVarietyPanel } from "./BreedVarietyPanel";

export interface IBreedVarietyListPageProps {
  breedId: number;
  editMode: boolean;
}

export const BreedVarietyListPage: React.FunctionComponent<IBreedVarietyListPageProps> = ({ breedId, editMode }) => {
  const api = useApiService(BreedVarietyApi);
  const { t } = useTl();
  const { currentLanguage, languages } = useGlobalData();

  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { breedGroupLabel: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: BreedVarietyAndExceptionSearch) => {
      const sobj = BreedVarietyAndExceptionSearch.fromJS({
        ...sObj,
        currentBreedId: breedId,
        languageId: currentLanguage.languageId,
      });
      setsobjState(sobj);
      return api.baseSearch(sobj);
    },
    [api, breedId, currentLanguage.languageId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { deleteItem } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.deleteBreedVariety(breedId, data),
        onDeletedFn: () => search(),
      }),
      [api, breedId, search]
    )
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: BreedVarietiesAndExceptionsGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.breedId)} />
          </ButtonContainer>
        ),
      },
      ...languages
        .filter((lg) => {
          return lg.languageId === currentLanguage.languageId;
        })
        .map((lg) => ({
          header: () => `${t(ETLCodes.Name)} ${lg.languageCode}`,
          computed: true,
          disableSorting: true,
          fieldName: "_lng" + lg.languageCode,
          render: (dto: BreedVarietiesAndExceptionsGridDto) => (
            <Link target="_blank" to={`${ERoutes.breeds}/${dto.breedId}/detail/view`}>
              {dto.breedNames.find((names) => names.code === lg.languageCode)?.text}
            </Link>
          ),
        })),
      {
        header: () => t(ETLCodes.BreedGroupLabel),
        fieldName: "breedGroupLabel",
        render: (row: BreedVarietiesAndExceptionsGridDto) => (
          <Link target="_blank" to={`${ERoutes.breedGroups}/${row.breedGroupId}/detail/view`}>
            {row.breedGroupLabel}
          </Link>
        ),
      },
    ],
    [currentLanguage.languageId, deleteItem, editMode, languages, t]
  );
  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);
  return (
    <>
      <FieldSet
        title={t(ETLCodes.BreedVarieties)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && (
              <EditButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.BreedVarietySelection)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1000, height: 150, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <BreedVarietyPanel
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
            breedId={breedId}
          />
        </div>
      </Dialog>
    </>
  );
};
